import { Suspense, useMemo } from 'react';
import Modal from './Modal';
import { Header } from './Header';
import { WatchBadge } from './WatchBadge';
import { badges } from '../resources/Letterboxd/watchProgress';
import {
  LetterboxdUserResponse,
  OneUserLetterboxdData,
} from '../resources/Letterboxd';
import { oscarDatabase, tmdbConfig } from '../config/constants';
import { tailwindVariables } from '../styles/tailwindVariables';
import { makeClassName } from '@siteless/util';
import { StarRating } from './StarRating';
import { HeartIcon } from './HeartIcon';
import ArrowUpRightIcon from './ArrowUpRightIcon';
import { Tooltip } from './Tooltip';
import { FilmOverview } from './FilmOverview';

export type BadgeKey = keyof typeof badges;

type FilmData = {
  id: string;
  title: string;
  tmdbId: number;
  posterUrl: string | null;
  releaseDate: string | null;
  nominationCount: number;
  nominations: string[];
  overview: string | null;
};

function getFilmsForBadge(badgeKey: BadgeKey): FilmData[] {
  let filmIds: string[] = [];

  if (badgeKey === 'bestPicture') {
    filmIds = Object.values(
      oscarDatabase.nomineeCategories.bestPicture.nominees,
    ).map((item) => item.filmId);
  } else {
    filmIds = Object.keys(oscarDatabase.films);
  }

  const nominationsByFilm = new Map<string, string[]>();
  for (const [, category] of Object.entries(oscarDatabase.nomineeCategories)) {
    for (const nominee of Object.values(category.nominees)) {
      const fId = (nominee as { filmId: string }).filmId;
      if (!nominationsByFilm.has(fId)) {
        nominationsByFilm.set(fId, []);
      }
      nominationsByFilm.get(fId)!.push(category.title);
    }
  }

  return filmIds
    .map((filmId) => {
      const film =
        oscarDatabase.films[filmId as keyof typeof oscarDatabase.films];
      if (!film) return null;

      const nominations = nominationsByFilm.get(filmId) || [];
      const posterPath = film.tmdb_data?.tmdb_poster_path;

      return {
        id: filmId,
        title: film.title,
        tmdbId: film.tmdb_data?.tmdb_id,
        posterUrl: posterPath
          ? [
              tmdbConfig.base_url,
              tmdbConfig.poster_sizes.w154,
              posterPath,
            ].join('')
          : null,
        releaseDate: film.tmdb_data?.tmdb_release_date || null,
        nominationCount: nominations.length,
        nominations,
        overview: film.tmdb_data?.tmdb_overview || null,
      };
    })
    .filter((f): f is FilmData => f !== null && f.tmdbId !== undefined);
}

function formatReleaseDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

const BadgeFilmRow = ({
  film,
  isWatched,
  letterboxdData,
  badgeColor,
}: {
  film: FilmData;
  isWatched: boolean;
  letterboxdData: LetterboxdUserResponse['oscarFilmsStatus'][string] | null;
  badgeColor: string;
}) => {
  const releaseDate = formatReleaseDate(film.releaseDate);
  const watchedDate = letterboxdData?.watchedDate
    ? formatReleaseDate(letterboxdData.watchedDate)
    : null;
  const nominationsText =
    film.nominationCount > 0
      ? `${film.nominationCount} nomination${
          film.nominationCount > 1 ? 's' : ''
        }`
      : '';
  const nominationsTooltip = film.nominations.join('\n');

  return (
    <a
      href={`https://letterboxd.com/tmdb/${film.tmdbId}/`}
      target="_blank"
      rel="noopener noreferrer"
      className={makeClassName(
        'flex gap-3 py-3 border-b border-neutral-100 last:border-b-0',
        !isWatched && 'opacity-75',
      )}
    >
      <div
        className="w-1 h-24 flex-none rounded-full"
        style={{
          backgroundColor: isWatched ? badgeColor : '#e0e4e3',
        }}
      />
      <div className="w-16 h-24 flex-none overflow-hidden rounded">
        {film.posterUrl ? (
          <img
            src={film.posterUrl}
            alt={film.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-neutral-100 flex items-center justify-center text-[8px] text-center p-1 text-neutral-400">
            {film.title}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-start">
        <div className="font-medium truncate flex items-center gap-1 justify-between">
          {film.title}
          <ArrowUpRightIcon className="h-3 w-3 flex-none text-neutral-400" />
        </div>
        {isWatched &&
          letterboxdData &&
          (letterboxdData.rating !== null || letterboxdData.liked) && (
            <div className="flex items-center gap-2 -mt-0.5 mb-0.5">
              {letterboxdData.rating !== null && (
                <StarRating
                  rating={letterboxdData.rating}
                  starClassName="text-rose-dark"
                  hideEmptyStars
                />
              )}
              {letterboxdData.liked && (
                <HeartIcon className="h-4 w-4 text-rose-default" />
              )}
            </div>
          )}
        <div className="text-xs text-neutral-500 flex items-center gap-1 mb-0.5 flex-wrap">
          {watchedDate ? (
            <span>Watched {watchedDate}</span>
          ) : (
            releaseDate && <span>Released {releaseDate}</span>
          )}
          {(watchedDate || releaseDate) && nominationsText && <span>•</span>}
          {nominationsText && (
            <span
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Tooltip
                text={nominationsTooltip}
                tooltipSide="bottom"
                className="cursor-pointer"
              >
                <span className="underline decoration-dotted">
                  {nominationsText}
                </span>
              </Tooltip>
            </span>
          )}
        </div>
        {film.overview && (
          <div className="text-sm text-neutral-500 leading-tight">
            <FilmOverview
              overview={film.overview}
              shortenTruncation={Boolean(
                isWatched &&
                  letterboxdData &&
                  (letterboxdData.rating !== null || letterboxdData.liked),
              )}
            />
          </div>
        )}
      </div>
    </a>
  );
};

const BadgeFilmsModalContent = ({
  badgeKey,
  letterboxdId,
}: {
  badgeKey: BadgeKey;
  letterboxdId: string;
}) => {
  const letterboxdResult = OneUserLetterboxdData.use(letterboxdId);
  const badge = badges[badgeKey];
  const films = useMemo(() => getFilmsForBadge(badgeKey), [badgeKey]);

  if (!letterboxdResult || letterboxdResult.status === 'error') {
    return (
      <div className="p-4 text-neutral-500">Unable to load Letterboxd data</div>
    );
  }

  const { oscarFilmsStatus } = letterboxdResult.data;
  const { watchedCount, totalCount } = badge.getProgress({ oscarFilmsStatus });

  const sortedFilms = useMemo(() => {
    const unwatchedCount = films.filter(
      (f) => !(oscarFilmsStatus[String(f.tmdbId)]?.watched ?? false),
    ).length;

    return [...films].sort((a, b) => {
      const aStatus = oscarFilmsStatus[String(a.tmdbId)];
      const bStatus = oscarFilmsStatus[String(b.tmdbId)];
      const aWatched = aStatus?.watched ?? false;
      const bWatched = bStatus?.watched ?? false;

      // Only show unwatched first when there are fewer than 5 remaining.
      // When a user has made little progress, pushing down their watched list
      // isn't useful. But when they're close to completing, surfacing the few
      // remaining films helps them see what's left.
      if (unwatchedCount < 5) {
        if (aWatched && !bWatched) return 1;
        if (!aWatched && bWatched) return -1;
      } else {
        if (aWatched && !bWatched) return -1;
        if (!aWatched && bWatched) return 1;
      }

      // Within watched films, sort by most recently watched first
      if (aWatched && bWatched) {
        const aDate = aStatus?.watchedDate;
        const bDate = bStatus?.watchedDate;
        if (aDate && bDate) return bDate.localeCompare(aDate);
        if (aDate && !bDate) return -1;
        if (!aDate && bDate) return 1;
      }

      return b.nominationCount - a.nominationCount;
    });
  }, [films, oscarFilmsStatus]);

  return (
    <div className="flex-1 overflow-auto">
      <div className="flex flex-col items-center gap-3 pt-8 pb-4 border-b border-neutral-100">
        <WatchBadge
          hideText
          renderArtwork={badge.renderBadgeArtwork}
          label={badge.label}
          size={80}
          notCompletedFillColor={tailwindVariables.theme.colors.neutral[200]}
          notCompletedLineColor={tailwindVariables.theme.colors.neutral[400]}
          totalCount={totalCount}
          completedCount={watchedCount}
          completedFillColor={badge.completedFillColor}
          completedLineColor={badge.completedLineColor}
        />
        <Header>{badge.label}</Header>
        <div className="text-sm text-neutral-500">
          <span className="font-medium text-purple-800">{watchedCount}</span> of{' '}
          <span className="font-medium text-purple-800">{totalCount}</span>{' '}
          watched
        </div>
      </div>
      <div className="px-4">
        {sortedFilms.map((film) => {
          const tmdbIdStr = String(film.tmdbId);
          const letterboxdData = oscarFilmsStatus[tmdbIdStr] || null;
          const isWatched = letterboxdData?.watched ?? false;

          return (
            <BadgeFilmRow
              key={film.id}
              film={film}
              isWatched={isWatched}
              letterboxdData={letterboxdData}
              badgeColor={badge.completedFillColor}
            />
          );
        })}
      </div>
    </div>
  );
};

const BadgeFilmsModal = ({
  badgeKey,
  letterboxdId,
  isOpen,
  onClose,
}: {
  badgeKey: BadgeKey | null;
  letterboxdId: string;
  isOpen: boolean;
  onClose: () => void;
}) => {
  if (!badgeKey) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} disableBodyPadding>
      <Suspense fallback={<Modal.Loader />}>
        <BadgeFilmsModalContent
          badgeKey={badgeKey}
          letterboxdId={letterboxdId}
        />
      </Suspense>
    </Modal>
  );
};

export default BadgeFilmsModal;
